import React, {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import EditGuestUserModal from './EditGuestUserModal';
import ClientContactModalProjectsLoader from './ClientContactModalProjectsLoader';
import Util from '../../shared/util/util';
import UpdatePersonMutation from '../../../mutations/update_person_mutation.modern';
import CreateProjectPersonClientMutation from '../../../mutations/create_project_person_client_mutation';
import DeleteProjectPersonMutation from '../../../mutations/delete_project_person_mutation';
import {createToast} from '../../shared/components/toasts/another-toast/toaster';
import type {ClientContactFormValues} from './hooks/useClientContactForm';

const TOAST_DURATION_MS = 5000;

export type EditGuestUserModalContainerProps = {
	personId: string;
	clientId: string;
	clientName: string;
	initialValues: ClientContactFormValues;
	projectPersonIdByProject: Map<string, string>;
	onClose: () => void;
};

const NETWORK_RETRY_DELAY_MS = 500;

const commit = <I,>(
	mutation: {commit: (env: unknown, input: I, onSuccess: () => void, ...rest: unknown[]) => unknown},
	input: I,
	attempt = 0
): Promise<void> =>
	new Promise((resolve, reject) => {
		// Pass an onError handler (6th arg) so failures reject this promise instead
		// of falling through to Util.CommitMutation's global "Oops!" error modal.
		// This lets the caller surface the spec'd snackbar and re-enable the form.
		// retry=false: we own the retry here (below) so Util doesn't also retry.
		Util.CommitMutation(
			mutation,
			input,
			() => resolve(),
			false,
			null,
			(transaction: unknown) => {
				// Mirror Util.CommitMutation's one-shot retry for a transient network blip.
				// Any other failure — or a second network failure — rejects so the caller
				// shows the snackbar rather than silently hanging.
				if (attempt < 1 && Util.isTransientNetworkError(String(transaction))) {
					setTimeout(() => {
						commit(mutation, input, attempt + 1).then(resolve, reject);
					}, NETWORK_RETRY_DELAY_MS);
				} else {
					reject(new Error('mutation failed'));
				}
			}
		);
	});

const EditGuestUserModalContainer = ({
	personId,
	clientId,
	clientName,
	initialValues,
	projectPersonIdByProject,
	onClose,
}: EditGuestUserModalContainerProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const submitLockRef = useRef(false);

	const handleSubmit = async (values: ClientContactFormValues) => {
		if (submitLockRef.current) return;
		submitLockRef.current = true;
		setIsSubmitting(true);

		const originalProjectIds = new Set(initialValues.projectIds ?? []);
		const nextProjectIds = new Set(values.projectIds ?? []);
		const toAdd = [...nextProjectIds].filter(id => !originalProjectIds.has(id));
		const toRemove = [...originalProjectIds].filter(id => !nextProjectIds.has(id));

		try {
			await commit(UpdatePersonMutation, {
				id: personId,
				firstName: values.firstName,
				lastName: values.lastName,
				email: values.email,
				// Send the raw value (incl. empty string) so clearing the phone
				// persists. `|| undefined` would omit it and the server keeps the old value.
				phone: values.phone,
			});

			await Promise.all(
				toAdd.map(projectId =>
					commit(CreateProjectPersonClientMutation, {
						personId,
						projectId,
					})
				)
			);

			await Promise.all(
				toRemove.map(projectId => {
					const projectPersonId = projectPersonIdByProject.get(projectId);
					if (!projectPersonId) return Promise.resolve();
					return commit(DeleteProjectPersonMutation, {
						ids: [projectPersonId],
						projectId,
					});
				})
			);

			submitLockRef.current = false;
			setIsSubmitting(false);
			createToast({
				duration: TOAST_DURATION_MS,
				message: formatMessage({id: 'client_contact.snackbar.guest_user_saved'}),
			});
			onClose();
		} catch {
			submitLockRef.current = false;
			setIsSubmitting(false);
			createToast({
				duration: TOAST_DURATION_MS,
				message: formatMessage({id: 'client_contact.snackbar.guest_user_save_failed'}),
			});
		}
	};

	return (
		<ClientContactModalProjectsLoader clientId={clientId}>
			{({projectOptions, isProjectsLoading}) => (
				<EditGuestUserModal
					clientName={clientName}
					projectOptions={projectOptions}
					isProjectsLoading={isProjectsLoading}
					initialValues={initialValues}
					onClose={onClose}
					onSubmit={handleSubmit}
					isSubmitting={isSubmitting}
				/>
			)}
		</ClientContactModalProjectsLoader>
	);
};

export default EditGuestUserModalContainer;
